import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import ServiceSelection from './components/ServiceSelection';
import PhoneNumberEntry from './components/PhoneNumberEntry';
import AppCodeEntry from './components/AppCodeEntry';
import QueueWarehouse from './components/QueueWarehouse';
import QueueDisplay from './components/QueueDisplay';
import QueueCreate from './components/QueueCreate';
import ProductNotFound from './components/ProductNotFound';
import QueueAlready from './components/QueueAlreadyBooked';
import OfflinePage from './components/OfflinePage';
import LanguageToggle from './components/LanguageToggle';
import queueApi from './services/api';
import signalRService from './services/signalR';
import { useNetworkStatus } from './utils/networkStatus';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { useAutoReload } from './hooks/useAutoReload';
import { formatTajikistanTime, formatTajikistanDate } from './utils/time';
import './App.css';

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOnline, isSlowConnection } = useNetworkStatus();
  const { language, changeLanguage, getText } = useLanguage();
  const isQueueNumberPage = location.pathname === '/display' || location.pathname === '/warehouse';
  const isOther = location.pathname === '/create' || location.pathname === '/product-not-found' || location.pathname === '/already'
      || location.pathname === '/phone-entry' || location.pathname === '/phone-entry';
  const isPhoneEntryPage = location.pathname === '/phone-entry' || location.pathname === '/app-code';

  useAutoReload();
  
  const [dateTime, setDateTime] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [token, setToken] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [terminalInfo, setTerminalInfo] = useState(null);
  const [footerClickCount, setFooterClickCount] = useState(0);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadTerminalInfo = async () => {
      if (isOnline) {
        try {
          const info = await queueApi.getTerminalInfo();
          if (info) {
            setTerminalInfo(info);
          }
        } catch (error) {
          console.error('Failed to load terminal info:', error);
        }
      }
    };

    loadTerminalInfo();
  }, [isOnline]);

  useEffect(() => {
    const initSignalR = async () => {
      if (isOnline && (location.pathname === '/display' || location.pathname === '/warehouse') && terminalInfo) {
        await signalRService.connect(terminalInfo.id);
      }
    };

    initSignalR();
  }, [location.pathname, terminalInfo, isOnline]);

  useEffect(() => {
    if (clickCount >= 3) {
      setShowModal(true);
      setClickCount(0);
    }
  }, [clickCount]);

  useEffect(() => {
    if (footerClickCount >= 3) {
      window.location.reload();
    }
  }, [footerClickCount]);

  useEffect(() => {
    setShowOfflineModal(!isOnline || isSlowConnection);
    
    if (!isOnline || isSlowConnection) {
      setWasOffline(true);
    }
  }, [isOnline, isSlowConnection, wasOffline]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();

    const handleTouchMove = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const timeStr = formatTajikistanTime(dateTime, false);
  const dateStr = formatTajikistanDate(dateTime);

  const handleLogoClick = () => {
    setClickCount(prev => prev + 1);
  };

  const handleBackToServices = () => {
    navigate('/');
  };

  const handleFooterClick = () => {
    setFooterClickCount(prev => prev + 1);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    try {
      queueApi.setToken(token);
      const response = await queueApi.getServices();
      if (response) {
        setShowModal(false);
        window.location.reload();
      } else {
        setModalError('Токен нодуруст аст ё сервер дастрас нест. Консолро тафтиш кунед.');
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setModalError('Хатои пайвастшавӣ ба сервер. Шабака ва URL-ро санҷед.');
    } finally {
      setModalLoading(false);
    }
  };

  if (isQueueNumberPage) {
    return (
        <Routes>
          <Route path="/warehouse" element={<QueueWarehouse />} />
          <Route path="/display" element={<QueueDisplay />} />
        </Routes>
    );
  }
  
  return (
    <LanguageProvider>
      <div className="app-container">
        <div className="bg-spotlight spotlight-1"></div>
        <div className="bg-spotlight spotlight-2"></div>

        
            <header className={`main-header ${showOfflineModal ? 'hidden' : ''}`}>
              {isPhoneEntryPage && (
                <button key={`back-btn-${language}`} className="back-to-services-btn" onClick={handleBackToServices}>
                  {getText('Назад к услугам', 'Ба қафо рафтан')}
                </button>
              )}
              <div className="logo" onClick={handleLogoClick}>FARMOISH</div>
              <div className="datetime-container">
                <div className="vertical-divider"></div>
                <div className="datetime-block">
                  <div className="current-time">{timeStr}</div>
                  <div className="current-date">{dateStr}</div>
                </div>
              </div>
            </header>
        <div className={`header-line ${showOfflineModal ? 'hidden' : ''}`}></div>

        <main className="content-wrapper">
          <Routes>
            <Route path="/" element={<ServiceSelection />} />
            <Route path="/phone-entry" element={<PhoneNumberEntry />} />
            <Route path="/app-code" element={<AppCodeEntry />} />
            <Route path="/warehouse" element={<QueueWarehouse />} />
            <Route path="/display" element={<QueueDisplay />} />
            <Route path="/create" element={<QueueCreate />} />
            <Route path="/already" element={<QueueAlready />} />
            <Route path="/product-not-found" element={<ProductNotFound />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

         {!isOther && (
            <footer className="app-footer">
              <div className="terminal-info-badge" onClick={handleFooterClick}>
                <div className="online-dot"></div>
                <span>{terminalInfo ? (terminalInfo.name) : 'Терминал'}</span>
              </div>
            </footer>
        )}
        

        {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div key={`modal-${language}`} className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{getText('API Key Configuration', 'Танзимоти калиди API')}</h2>
                  <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                </div>
                <form onSubmit={handleModalSubmit}>
                  <div className="modal-body">
                    <div className="input-group">
                      <label>{getText('Введите ваш API ключ:', 'Калиди API-и худро ворид кунед:')}</label>
                      <input
                          type="text"
                          className="input-field"
                          placeholder="API Key"
                          value={token}
                          onChange={e => setToken(e.target.value)}
                          required
                      />
                    </div>
                    {modalError && <div className="error-message">{modalError}</div>}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="button button-cancel" onClick={() => setShowModal(false)}>{getText('Отмена', 'Бекор кардан')}</button>
                    <button type="submit" className="button button-submit" disabled={modalLoading}>
                      {modalLoading ? getText('Сохранение...', 'Нигоҳ доштан...') : getText('Сохранить', 'Нигоҳ доред')}
                    </button>
                  </div>
                </form>
                {/*<LanguageToggle />*/}
              </div>
            </div>
        )}

        {showOfflineModal && <OfflinePage />}
      </div>
    </LanguageProvider>
  );
}

export default App;